import React, {useState} from 'react';
import {BaseModal} from 'components/modals';
import Button from '@/components/ui/Button';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {buildURL} from '@/utils/urlBuilder';

interface ActionLog {
    [key: string]: any;
}

interface ActionLogItem {
    actionTypeIdentifierDisplay: string;
    actionTitle: string;
    createdOn: string;
    statusDisplay: string;
    actionLog: ActionLog;
}

interface ActionLogsResponse {
    results: ActionLogItem[];
}

interface ActionLogsTableProps {
    eventContentId: string | number;
    eventType: string;
}

const ActionLogsTable: React.FC<ActionLogsTableProps> = React.memo((
    {
        eventContentId,
        eventType,
    },
) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [detailsModalOpen, setDetailsModalOpen] = useState<boolean>(false);
    const [details, setDetails] = useState<ActionLog>({});

    const [actionLogs, loading] = useData<ActionLogsResponse>(buildURL('eventActionLogs'), {
        useLocalStore: true,
        requestParams: {
            params: {
                [`event__event_${eventType}`]: eventContentId,
            },
        },
    });

    if (loading) {
        return (
            <CellLoader fullHeight={true} />
        );
    }

    if (actionLogs.results.length === 0) {
        return (
            <span className="m-3">
                {i18n.t('jt_data_available.text')}
            </span>
        )
    }

    const columns = [
        {
            header: i18n.t('th_action_type.text'),
            accessorKey: 'actionTypeIdentifierDisplay',
        },
        {
            header: i18n.t('th_action_name.text'),
            accessorKey: 'actionTitle',
        },
        {
            header: i18n.t('th_date_created.text'),
            accessorKey: 'createdOn',
            cell: (cell: { row: { original: ActionLogItem } }) => {
                return configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_status.text'),
            accessorKey: 'statusDisplay',
        },
        {
            header: i18n.t('th_additional_details.text'),
            cell: (cell: { row: { original: ActionLogItem } }) => {
                if (Object.keys(cell.row.original.actionLog).length) {
                    return (

                        <Button
                            variant="info"
                            size="sm"
                            onClick={() => {
                                setDetails(cell.row.original.actionLog);
                                setDetailsModalOpen(true);}}
                        >
                            {i18n.t('jt_show.text')}
                        </Button>
                    );
                }

                return null;
            },
        },
    ];

    return (
        <>
            <div className="card">
                <div className="card-body">
                    <TableComponent data={actionLogs.results} columns={columns}/>
                </div>
            </div>
            <BaseModal
                isOpen={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
                title={i18n.t('jt_action_log.text')}
                size="xl"
            >
                <pre>
                    {JSON.stringify(details, null, 4)}
                </pre>
            </BaseModal>
        </>
    );
});


export default ActionLogsTable; 